/**
 * Confirms disabling or enabling a program. Disabling takes effect for the next recommendation; the
 * program is not deleted and can be enabled again.
 */
import { useState } from 'react';
import { Alert, Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';

import {
  getCpaClient,
  type InvestmentProgramData,
  UpdateInvestmentProgramStatusCommand,
} from '../../client/CpaClient';
import { saveErrorOf } from '../../utils/programForm';

export default function ProgramStatusDialog({
  program,
  onClose,
  onDone,
}: {
  program: InvestmentProgramData;
  onClose: () => void;
  onDone: (updated: InvestmentProgramData) => void;
}) {
  const { t } = useTranslation('cpa');
  const tx = t as unknown as (key: string) => string;
  const disable = program.status !== 'disabled';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ key: string; detail?: string } | null>(null);

  const confirm = () => {
    setBusy(true);
    setError(null);
    getCpaClient()
      .send(
        new UpdateInvestmentProgramStatusCommand({
          programId: program.programId,
          status: disable ? 'disabled' : 'active',
          expectedUpdatedAt: program.updatedAt,
        }),
      )
      .then((out) => onDone(out.program!))
      .catch((e: unknown) => setError(saveErrorOf(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={t(disable ? 'statusDisableTitle' : 'statusEnableTitle', { name: program.name })}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={busy}>
              {t('cancel')}
            </Button>
            <Button variant="primary" loading={busy} onClick={confirm}>
              {t(disable ? 'statusDisable' : 'statusEnable')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type={disable ? 'warning' : 'info'}>
          {t(disable ? 'statusDisableBody' : 'statusEnableBody')}
        </Alert>
        {error && (
          <Alert type="error" header={tx(error.key)}>
            {error.detail}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
}
